import { useEffect, useMemo, useRef, useState } from "react";
import {
  LIST_SEARCH_DEBOUNCE_MS,
  readQ,
  setOrDelete,
} from "@/modules/shell/list-url-filters";
import { useListUrlParams } from "@/modules/shell/use-list-url-params";
import { Link, useNavigate, useParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { SortingState } from "@tanstack/react-table";
import { useForm } from "react-hook-form";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  partyCreateSchema,
  type PartyCreateInput,
} from "@/modules/parties/party-form";
import { ContactGlancePeek } from "@/modules/parties/components/ContactGlancePeek";
import { contactsColumns } from "@/modules/parties/contacts-columns";
import { AppPicker } from "@/modules/shell/AppPicker";
import {
  AdminFilters,
  adminFilterControlClass,
} from "@/modules/shell/AdminFilters";
import { DataTable } from "@/modules/shell/data-table";
import { EmptyState } from "@/modules/shell/EmptyState";
import {
  DataTableChrome,
  DeskListFilterReset,
} from "@/modules/shell/DataTableChrome";
import { DETAIL_COMPACT_MQ } from "@/modules/shell/detail-edit-mode";
import { useDeskListFilterReset } from "@/modules/shell/use-desk-list-filter-reset";
import {
  ListSearchStatus,
  listSearchOnlinePending,
} from "@/modules/shell/ListSearchStatus";
import { DeskListHeader } from "@/modules/shell/DeskListHeader";
import { PageFrame } from "@/modules/shell/PageFrame";
import { SavedListViewsControl } from "@/modules/shell/SavedListViewsControl";
import { takeListReturn } from "@/modules/shell/list-return-state";
import { useListRowFlash } from "@/modules/shell/use-list-row-flash";
import { cn } from "@/lib/utils";
import {
  deskSegmentItemClass,
  deskSegmentListClass,
  deskSegmentNavClass,
} from "@/modules/shell/desk-segment";
import { listPriceLists } from "@/shared/api/inventory";
import {
  createParty,
  listParties,
  type PartyListSort,
  type PartyRoleFilter,
} from "@/shared/api/parties";
import { notify } from "@/shared/feedback/notify";
import { queryKeys } from "@/shared/query/keys";

const PAGE_SIZE = 50;
const SORTABLE: PartyListSort[] = ["name", "code"];

function parseRole(raw: string | null): PartyRoleFilter {
  if (raw === "customer" || raw === "supplier") return raw;
  return "all";
}

function sortingToApi(sorting: SortingState): {
  sort: PartyListSort;
  sortDir: "asc" | "desc";
} {
  const first = sorting[0];
  if (first && SORTABLE.includes(first.id as PartyListSort)) {
    return {
      sort: first.id as PartyListSort,
      sortDir: first.desc ? "desc" : "asc",
    };
  }
  return { sort: "name", sortDir: "asc" };
}

const ROLE_TABS: { id: PartyRoleFilter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "customer", label: "Customers" },
  { id: "supplier", label: "Suppliers" },
];

export function ContactsPage() {
  const { companySlug } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { searchParams, patchParams, replaceAllParams } = useListUrlParams();
  const role = parseRole(searchParams.get("role"));
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [query, setQuery] = useState(() => readQ(searchParams));
  const [debouncedQ, setDebouncedQ] = useState(() => readQ(searchParams));
  const [sorting, setSorting] = useState<SortingState>([]);
  const [glanceId, setGlanceId] = useState<string | null>(null);
  const [compact, setCompact] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia(DETAIL_COMPACT_MQ).matches
      : true,
  );
  const { sort, sortDir } = sortingToApi(sorting);
  const { flashId, flashRow } = useListRowFlash();
  const listReturnHandled = useRef(false);

  const listPath = companySlug
    ? `/c/${companySlug}/contacts${searchParams.toString() ? `?${searchParams}` : ""}`
    : "";

  useEffect(() => {
    const mq = window.matchMedia(DETAIL_COMPACT_MQ);
    const onChange = () => setCompact(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    const t = window.setTimeout(() => {
      const q = query.trim();
      setDebouncedQ(q);
      patchParams((p) => setOrDelete(p, "q", q));
    }, LIST_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [query, patchParams]);

  const partiesQuery = useInfiniteQuery({
    queryKey: queryKeys.parties.list(companySlug ?? "", {
      role,
      includeDeactivated: true,
      q: debouncedQ,
      limit: PAGE_SIZE,
      sort,
      sortDir,
    }),
    enabled: Boolean(companySlug),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) =>
      listParties(companySlug!, {
        role,
        includeDeactivated: true,
        q: debouncedQ || undefined,
        limit: PAGE_SIZE,
        cursor: pageParam,
        sort,
        sortDir,
        signal,
      }),
    getNextPageParam: (last) =>
      last.hasMore && last.nextCursor ? last.nextCursor : undefined,
  });
  const priceListsQuery = useQuery({
    queryKey: queryKeys.priceLists.list(companySlug ?? ""),
    enabled: Boolean(companySlug),
    queryFn: () => listPriceLists(companySlug!),
  });

  const loadError =
    (partiesQuery.error instanceof Error
      ? partiesQuery.error.message
      : partiesQuery.isError
        ? "Failed"
        : null) ??
    (priceListsQuery.error instanceof Error
      ? priceListsQuery.error.message
      : priceListsQuery.isError
        ? "Failed"
        : null);

  useEffect(() => {
    if (loadError === "Not signed in") {
      navigate("/login", { replace: true });
    } else if (loadError) {
      setError(loadError);
    }
  }, [loadError, navigate]);

  useEffect(() => {
    if (!companySlug || partiesQuery.isPending || listReturnHandled.current) {
      return;
    }
    listReturnHandled.current = true;
    const id = takeListReturn(`/c/${companySlug}/contacts`);
    if (id) flashRow(id);
  }, [companySlug, partiesQuery.isPending, flashRow]);

  async function reload() {
    if (!companySlug) return;
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: queryKeys.parties.all(companySlug),
      }),
      queryClient.invalidateQueries({
        queryKey: queryKeys.customers.all(companySlug),
      }),
      queryClient.invalidateQueries({
        queryKey: queryKeys.suppliers.all(companySlug),
      }),
      queryClient.invalidateQueries({
        queryKey: queryKeys.priceLists.all(companySlug),
      }),
    ]);
  }

  const createForm = useForm<PartyCreateInput>({
    resolver: zodResolver(partyCreateSchema),
    defaultValues: {
      name: "",
      isCustomer: role !== "supplier",
      isSupplier: role === "supplier",
      priceListId: "",
    },
  });

  useEffect(() => {
    createForm.reset({
      name: "",
      isCustomer: role !== "supplier",
      isSupplier: role === "supplier",
      priceListId: "",
    });
  }, [role, createForm]);

  const create = useMutation({
    mutationFn: async (raw: PartyCreateInput) => {
      const body = partyCreateSchema.parse(raw);
      return createParty(companySlug!, {
        name: body.name,
        isCustomer: body.isCustomer,
        isSupplier: body.isSupplier,
        ...(body.isCustomer && body.priceListId
          ? { priceListId: body.priceListId }
          : {}),
      });
    },
    onSuccess: async (res, vars) => {
      createForm.reset({
        name: "",
        isCustomer: role !== "supplier",
        isSupplier: role === "supplier",
        priceListId: "",
      });
      setShowAdd(false);
      setError(null);
      notify.added(vars.name.trim() || "contact");
      await reload();
      if (companySlug && res.party?.id) {
        navigate(`/c/${companySlug}/contacts/${res.party.id}`);
      }
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : "Failed";
      setError(msg);
      notify.error(msg);
    },
  });

  const rows = partiesQuery.data?.pages.flatMap((p) => p.parties) ?? [];
  const total = partiesQuery.data?.pages[0]?.total;
  const priceLists = priceListsQuery.data?.priceLists ?? [];
  const watchCustomer = createForm.watch("isCustomer");

  function listLabel(id: string | null) {
    if (!id) return "Org default";
    return priceLists.find((l) => l.id === id)?.name ?? "List";
  }

  const columns = useMemo(
    () =>
      companySlug
        ? contactsColumns({
            companySlug,
            listPath,
            listLabel,
            // Phone → full detail; desk → glance peek (plan/45).
            onGlance: compact ? undefined : (id) => setGlanceId(id),
          })
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- listLabel closes over priceLists
    [companySlug, listPath, priceLists, compact],
  );

  const filtersActive = Boolean(query.trim()) || role !== "all";
  const filterReset = useDeskListFilterReset({
    patchParams,
    setQuery: setQuery,
    canResetToDefault: filtersActive,
    canClearAll: filtersActive,
  });
  const searchingOnline = listSearchOnlinePending({
    query,
    debouncedQ,
    isFetching: partiesQuery.isFetching,
  });

  if (!companySlug) return null;

  const emptyTitle =
    role === "customer"
      ? "No customers yet"
      : role === "supplier"
        ? "No suppliers yet"
        : "No contacts yet";

  return (
    <PageFrame>
      <DataTableChrome>
        <DeskListFilterReset {...filterReset} />
        <DeskListHeader
          title="Contacts"
          description="One party can be a customer, a supplier, or both. Desk click glances; View more opens the full contact."
          tabs={
            <nav
              aria-label="Contact roles"
              className={deskSegmentNavClass(
                "mt-0 max-w-[min(100vw-8rem,28rem)]",
              )}
            >
              <div className={deskSegmentListClass()} role="tablist">
                {ROLE_TABS.map((tab) => {
                  const active = role === tab.id;
                  const to =
                    tab.id === "all"
                      ? `/c/${companySlug}/contacts`
                      : `/c/${companySlug}/contacts?role=${tab.id}`;
                  return (
                    <Link
                      key={tab.id}
                      to={to}
                      role="tab"
                      aria-selected={active}
                      className={cn(
                        deskSegmentItemClass(active),
                        "no-underline",
                      )}
                    >
                      {tab.label}
                    </Link>
                  );
                })}
              </div>
            </nav>
          }
        />
        <div className="mt-6">
          {!showAdd ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setShowAdd(true)}
            >
              Add contact
            </Button>
          ) : (
            <form
              className="space-y-2 rounded-xl border border-[var(--color-border)] bg-[var(--color-background)]/60 px-3.5 py-3"
              onSubmit={createForm.handleSubmit((values) =>
                create.mutate(values),
              )}
            >
              <div className="flex items-center justify-between gap-2">
                <p className="m-0 text-sm font-medium">New contact</p>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setShowAdd(false);
                    createForm.reset({
                      name: "",
                      isCustomer: role !== "supplier",
                      isSupplier: role === "supplier",
                      priceListId: "",
                    });
                  }}
                >
                  Cancel
                </Button>
              </div>
              <div className="flex gap-2">
                <Input
                  className="flex-1"
                  placeholder="Name"
                  autoFocus
                  {...createForm.register("name")}
                />
                <Button type="submit" disabled={create.isPending}>
                  Create
                </Button>
              </div>
              {createForm.formState.errors.name && (
                <p className="text-xs text-[var(--color-destructive)]">
                  {createForm.formState.errors.name.message}
                </p>
              )}
              <div className="flex flex-wrap gap-4 text-sm">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={createForm.watch("isCustomer")}
                    onChange={(e) =>
                      createForm.setValue("isCustomer", e.target.checked, {
                        shouldDirty: true,
                        shouldValidate: true,
                      })
                    }
                  />
                  Customer
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={createForm.watch("isSupplier")}
                    onChange={(e) =>
                      createForm.setValue("isSupplier", e.target.checked, {
                        shouldDirty: true,
                        shouldValidate: true,
                      })
                    }
                  />
                  Supplier
                </label>
              </div>
              {createForm.formState.errors.isCustomer && (
                <p className="text-xs text-[var(--color-destructive)]">
                  {createForm.formState.errors.isCustomer.message}
                </p>
              )}
              {watchCustomer ? (
                <div>
                  <Label htmlFor="party-new-price-list">Default price list</Label>
                  <AppPicker
                    id="party-new-price-list"
                    className="mt-1"
                    title="Price list"
                    description="Default list for new sales orders"
                    placeholder="Org default"
                    clearLabel="Org default"
                    value={createForm.watch("priceListId") || null}
                    options={priceLists.map((l) => ({
                      value: l.id,
                      label: l.name,
                      keywords: l.name,
                    }))}
                    emptyLabel="No price lists"
                    onChange={(id) =>
                      createForm.setValue("priceListId", id ?? "", {
                        shouldDirty: true,
                      })
                    }
                  />
                </div>
              ) : null}
              <p className="m-0 text-xs text-[var(--color-muted)]">
                Add phones, emails, and addresses on the contact after create.
              </p>
            </form>
          )}
        </div>
        {error && (
          <Alert variant="destructive" className="mt-2">
            {error}
          </Alert>
        )}
        <AdminFilters>
          <Input
            className={`${adminFilterControlClass} min-w-[12rem] flex-1`}
            placeholder="Search name or code…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <SavedListViewsControl
            listId="contacts"
            companySlug={companySlug}
            currentQs={searchParams.toString()}
            onApplyQs={(qs) => {
              replaceAllParams(qs);
              const q = readQ(new URLSearchParams(qs));
              setQuery(q);
              setDebouncedQ(q);
            }}
          />
        </AdminFilters>
        <ListSearchStatus online searching={searchingOnline} />
        {rows.length > 0 && total != null ? (
          <p className="mt-2 text-xs text-[var(--color-muted)]">
            Showing {rows.length}
            {total > rows.length ? ` of ${total}` : ""}
          </p>
        ) : null}
        {partiesQuery.isPending && (
          <Alert variant="muted" className="mt-8">
            Loading contacts…
          </Alert>
        )}
        {!partiesQuery.isPending && rows.length === 0 && !error ? (
          <div className="mt-8">
            <EmptyState
              title={
                debouncedQ ? `No match for “${debouncedQ}”` : emptyTitle
              }
              description={
                debouncedQ
                  ? "Try another name or code."
                  : "Add a contact for sales, purchasing, or both."
              }
              action={
                filtersActive ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => filterReset.onClearAll()}
                  >
                    Clear filters
                  </Button>
                ) : undefined
              }
            />
          </div>
        ) : null}
        {rows.length > 0 ? (
          <DataTable
            listId="contacts"
            companySlug={companySlug}
            columns={columns}
            data={rows}
            getRowId={(r) => r.id}
            sorting={sorting}
            onSortingChange={setSorting}
            defaultVisibility={{ code: false, priceList: false }}
            highlightedRowId={flashId}
            onRefresh={() => void partiesQuery.refetch()}
          />
        ) : null}
        {partiesQuery.hasNextPage ? (
          <div className="mt-4 flex justify-center">
            <Button
              type="button"
              variant="outline"
              disabled={partiesQuery.isFetchingNextPage}
              pending={partiesQuery.isFetchingNextPage}
              pendingLabel="Loading more…"
              onClick={() => void partiesQuery.fetchNextPage()}
            >
              Load more
            </Button>
          </div>
        ) : null}
        <p className="mt-8 text-sm">
          <Link
            to={`/c/${companySlug}/sales/orders`}
            className="no-underline"
          >
            Sales orders
          </Link>
          {" · "}
          <Link
            to={`/c/${companySlug}/purchasing/orders`}
            className="no-underline"
          >
            Purchase orders
          </Link>
          {" · "}
          <Link
            to={`/c/${companySlug}/settings/price-lists`}
            className="no-underline"
          >
            Price lists
          </Link>
        </p>
      </DataTableChrome>

      <ContactGlancePeek
        open={Boolean(glanceId) && !compact}
        partyId={glanceId}
        companySlug={companySlug}
        listPath={`/c/${companySlug}/contacts`}
        onOpenChange={(open) => {
          if (!open) setGlanceId(null);
        }}
      />
    </PageFrame>
  );
}
